import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { WhatsAppOutlined, InstagramFilled, FacebookFilled } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ContactList from '../components/ContactList';
import MessageList from '../components/MessageList';
import MessageInput from '../components/MessageInput';
import NewConversationModal from '../components/NewConversationModal';
import ContactSidebar from '../components/ContactSidebar';
import { useWebSocket } from '../hooks/useWebSocket';
import { useMessages } from '../hooks/useMessages';
import { useToast } from '../components/Toast';
import WebSocketService from '../services/websocket';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { instancesAPI, notificacoesAPI } from '../services/api';
import { User } from 'lucide-react';

const Atendimento = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contatoAtivo, setContatoAtivo] = useState(null);
  const [mostrarModalNovaConversa, setMostrarModalNovaConversa] = useState(false);
  const [mostrarSidebar, setMostrarSidebar] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [iaPausada, setIaPausada] = useState(false);
  const [instanciaSelecionada, setInstanciaSelecionada] = useState(null);

  // Sincronizar iaPausada quando contatoAtivo muda
  useEffect(() => {
    setIaPausada(contatoAtivo?.ia_pausada || false);
  }, [contatoAtivo?.telefone, contatoAtivo?.ia_pausada]);
  const [instanciaFiltro, setInstanciaFiltro] = useState(null); // null = todas
  const [instancias, setInstancias] = useState([]);
  const [filtroAtendente, setFiltroAtendente] = useState('todas'); // 'todas' | 'minhas' | 'nao_atribuidas'
  const [atendentesDigitando, setAtendentesDigitando] = useState({}); // { telefone: usuario_id }
  const scrollToBottomRef = useRef(null);
  const deepLinkProcessed = useRef(false);
  
  const token = localStorage.getItem('token');
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const toast = useToast();
  
  // Custom hooks para gerenciar WebSocket e mensagens
  const { conectado, conversasAgrupadas, carregandoConversas, erroEnvio, trocarCanal } =
    useWebSocket(token, instanciaFiltro);

  // Sincronizar contatoAtivo com conversasAgrupadas atualizado
  useEffect(() => {
    if (!contatoAtivo) return;
    const chave = `${contatoAtivo.telefone}_${contatoAtivo.instancia_id}`;
    const atualizado = conversasAgrupadas[chave];
    if (atualizado && atualizado.ia_pausada !== contatoAtivo.ia_pausada) {
      setContatoAtivo(prev => ({ ...prev, ia_pausada: atualizado.ia_pausada }));
    }
  }, [conversasAgrupadas]);

  useEffect(() => {
    if (erroEnvio) toast.error(erroEnvio, 'Falha no envio');
  }, [erroEnvio]);

  // Abrir conversa via query param ?telefone=&instancia=
  useEffect(() => {
    if (deepLinkProcessed.current) return;
    const tel = searchParams.get('telefone');
    const inst = searchParams.get('instancia');
    if (!tel || Object.keys(conversasAgrupadas).length === 0) return;

    const contato = inst
      ? conversasAgrupadas[`${tel}_${inst}`]
      : Object.values(conversasAgrupadas).find(c => c.telefone === tel);
    if (contato) {
      setContatoAtivo(contato);
      deepLinkProcessed.current = true;
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, conversasAgrupadas]);
  const {
    mensagens,
    carregando,
    temMais,
    novasMensagens,
    mostrarNotificacao,
    carregarMaisMensagens,
    enviarMensagem,
    setNovasMensagens,
    setMostrarNotificacao,
    forceUpdate
  } = useMessages(contatoAtivo);

  // Carregar instâncias conectadas
  useEffect(() => {
    const loadInstances = async () => {
      try {
        const response = await instancesAPI.list(user.cliente_id);
        const conectadas = (response.data || []).filter(i => i.status === 'conectada');
        setInstancias(conectadas);
        if (conectadas.length > 0 && !instanciaSelecionada) {
          setInstanciaSelecionada(conectadas[0].id);
        }
      } catch (error) {
        console.error('Erro ao carregar instâncias:', error);
      }
    };
    if (user?.cliente_id) {
      loadInstances();
    }
  }, [user]);

  // Callbacks memoizados para evitar re-renderizações
  const handleSelectContact = useCallback((contato) => {
    setContatoAtivo(contato);
    setShowChat(true);

    // Não há recarga da lista aqui: `respondida` só muda quando alguém responde,
    // então recarregar voltaria com o mesmo dado. O PATCH abaixo só toca
    // `notificacoes`, que a lista não exibe.
    notificacoesAPI.marcarLidaPorContato(contato.telefone).catch(() => {});
  }, []);

  const handleLoadMore = useCallback(() => {
    return carregarMaisMensagens();
  }, [carregarMaisMensagens]);

  // Estes três eram arrows inline no JSX, o que recriava a prop a cada render e
  // anulava o React.memo do ContactList.
  const handleRefreshLista = useCallback(
    () => WebSocketService.recarregarConversas(instanciaFiltro, { imediato: true }),
    [instanciaFiltro],
  );

  const handleNovaConversa = useCallback(
    () => setMostrarModalNovaConversa(true),
    [],
  );

  const handleSendMessage = useCallback((texto) => {
    // Prioridade: 1) Instância da conversa ativa, 2) Instância do filtro, 3) Primeira disponível
    const instanciaParaEnvio = contatoAtivo?.instancia_id || instanciaFiltro || instanciaSelecionada;
    
    enviarMensagem(texto, () => {
      // Scroll para o final após enviar
      if (scrollToBottomRef.current) {
        scrollToBottomRef.current();
      }
    }, instanciaParaEnvio);
    setIaPausada(true); // IA pausada após enviar mensagem
  }, [enviarMensagem, instanciaFiltro, instanciaSelecionada, contatoAtivo]);

  const handleClearNotification = useCallback(() => {
    setMostrarNotificacao(false);
    setNovasMensagens(0);
  }, [setMostrarNotificacao, setNovasMensagens]);

  const handleStartConversation = useCallback((novoContato) => {
    // Se veio com instancia_id do modal, setar como filtro pra enviar por ela
    if (novoContato.instancia_id) {
      setInstanciaFiltro(novoContato.instancia_id);
    }
    setContatoAtivo(novoContato);
  }, []);

  const handleFiltroInstancia = useCallback((instanciaId) => {
    setInstanciaFiltro(instanciaId);
    setContatoAtivo(null);
    // Ação do usuário e muda o conjunto de dados (o backend filtra no SQL).
    trocarCanal(instanciaId);
  }, [trocarCanal]);

  // Listener para eventos de atribuição e digitação
  useEffect(() => {
    const socket = WebSocketService.getSocket?.();
    if (!socket) return;

    const onAtendenteDigitando = (data) => {
      setAtendentesDigitando(prev => ({ ...prev, [data.telefone]: data.nome || 'Atendente' }));
      // Limpar após 3 segundos
      setTimeout(() => {
        setAtendentesDigitando(prev => {
          const next = { ...prev };
          delete next[data.telefone];
          return next;
        });
      }, 3000);
    };

    // conversa_atribuida é tratado por patch local no useWebSocket, que agora
    // recebe instancia_id do gateway e consegue achar a chave certa.
    socket.on('atendente_digitando', onAtendenteDigitando);

    return () => {
      socket.off('atendente_digitando', onAtendenteDigitando);
    };
  }, []);

  // Sem useMemo, este array é recriado a cada render e o React.memo do
  // ContactList nunca segura — a lista inteira re-renderiza a cada mensagem.
  const contatos = useMemo(
    () =>
      Object.values(conversasAgrupadas).filter((contato) => {
        if (filtroAtendente === 'minhas') return contato.atendente_id === user?.id;
        if (filtroAtendente === 'nao_atribuidas') return !contato.atendente_id;
        return true;
      }),
    [conversasAgrupadas, filtroAtendente, user?.id],
  );

  const permiteNovaConversa = useMemo(() => {
    if (!instanciaFiltro) return true;
    const plataforma = instancias.find((i) => i.id === instanciaFiltro)?.platform;
    return !['instagram', 'facebook'].includes(plataforma);
  }, [instanciaFiltro, instancias]);

  return (
    <Layout fullWidth>
      <div className={`h-[calc(100dvh-92px)] flex flex-col overflow-hidden ${
        darkMode ? 'bg-zinc-900' : 'bg-gray-100'
      }`}>
        {/* Abas de filtro por instância */}
        {instancias.length > 1 && (
          <div className={`${showChat ? 'hidden md:flex' : 'flex'} gap-1.5 sm:gap-2 p-2 sm:p-3 border-b overflow-x-auto ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
            <button
              onClick={() => handleFiltroInstancia(null)}
              className={`px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                instanciaFiltro === null
                  ? darkMode
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : darkMode
                    ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📬 Todas
            </button>
            {instancias.map(inst => (
              <button
                key={inst.id}
                onClick={() => handleFiltroInstancia(inst.id)}
                className={`px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                  instanciaFiltro === inst.id
                    ? inst.platform === 'instagram'
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                      : inst.platform === 'facebook'
                        ? 'bg-blue-600 text-white'
                        : 'bg-nomoz-navy text-white'
                    : darkMode
                      ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {inst.platform === 'instagram'
                  ? <><InstagramFilled style={{ fontSize: 16 }} /> @{inst.instagram_username || inst.nome_instancia}</>
                  : inst.platform === 'facebook'
                    ? <><FacebookFilled style={{ fontSize: 16 }} /> {inst.nome_instancia}</>
                    : <><WhatsAppOutlined style={{ fontSize: 16 }} /> {inst.nome_instancia}</>
                }
              </button>
            ))}
          </div>
        )}
        
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <div className={`flex flex-col min-h-0 overflow-hidden w-full md:w-auto ${showChat ? 'hidden md:flex' : 'flex'}`}>
            {/* Filtro por atendente */}
            <div className={`flex gap-1 px-3 py-2 border-b ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
              {[
                { key: 'todas', label: 'Todas' },
                { key: 'minhas', label: 'Minhas' },
                { key: 'nao_atribuidas', label: 'Livres' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFiltroAtendente(f.key)}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    filtroAtendente === f.key
                      ? 'bg-nomoz-orange text-white'
                      : darkMode
                        ? 'text-gray-400 hover:text-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <ContactList
              contatos={contatos}
              carregando={carregandoConversas}
              contatoAtivo={contatoAtivo}
              onSelectContact={handleSelectContact}
              conectado={conectado}
              onRefresh={handleRefreshLista}
              onNewConversation={permiteNovaConversa ? handleNovaConversa : null}
              darkMode={darkMode}
              instanciaFiltro={instanciaFiltro}
              currentUserId={user?.id}
              atendentesDigitando={atendentesDigitando}
            />
          </div>

          <div className={`flex-1 flex flex-col min-h-0 overflow-hidden ${!showChat ? 'hidden md:flex' : 'flex'}`}>
          <MessageList
            contatoAtivo={contatoAtivo}
            onBack={() => setShowChat(false)}
            mensagens={mensagens}
            carregando={carregando}
            onLoadMore={handleLoadMore}
            novasMensagens={novasMensagens}
            mostrarNotificacao={mostrarNotificacao}
            onClearNotification={handleClearNotification}
            onIaStatusChange={setIaPausada}
            iaPausadaProp={iaPausada}
            onToggleSidebar={() => setMostrarSidebar(!mostrarSidebar)}
            sidebarAberta={mostrarSidebar}
            onReenviar={(conv) => {
              WebSocketService.enviarMensagemDireta({
                telefone: conv.telefone,
                mensagem: conv.conteudo || conv.resposta_enviada,
                instanciaId: contatoAtivo?.instancia_id,
              });
            }}
            darkMode={darkMode}
            scrollToBottomRef={scrollToBottomRef}
          />
          
          {contatoAtivo && (
            <MessageInput
              onSendMessage={handleSendMessage}
              disabled={!conectado}
              darkMode={darkMode}
              conversaId={contatoAtivo?.ultima_conversa_id}
              instanciaId={contatoAtivo?.instancia_id}
              onMediaSent={() => {
                if (scrollToBottomRef.current) scrollToBottomRef.current();
                setIaPausada(true);
              }}
              onTyping={() => {
                const socket = WebSocketService.getSocket?.();
                if (socket && contatoAtivo) {
                  socket.emit('digitando', { telefone: contatoAtivo.telefone, instanciaId: contatoAtivo.instancia_id, nome: user?.nome });
                }
              }}
            />
          )}
        </div>

        {/* Contact Sidebar */}
        {mostrarSidebar && contatoAtivo && (
          <ContactSidebar
            telefone={contatoAtivo.telefone}
            nomeContato={contatoAtivo.nome}
            clienteId={user?.cliente_id}
            onClose={() => setMostrarSidebar(false)}
          />
        )}
      </div>
    </div>
      
      <NewConversationModal
        isOpen={mostrarModalNovaConversa}
        onClose={() => setMostrarModalNovaConversa(false)}
        onStartConversation={handleStartConversation}
        token={token}
        darkMode={darkMode}
        instancias={instancias}
      />
    </Layout>
  );
};

export default Atendimento;